import { getCookie, setCookie, deleteCookie } from "@/lib/cookies";
import { PORTAL_HOME_URL, OAUTH_CLIENT_ID, OAUTH_LOGIN_TAG } from "@/lib/portal";
import type { TokenData, SsoResponse, UserInfo } from "@/types";
import { APP_CONFIG } from "@/lib/config";

const OAUTH_TOKEN_KEY = "auth_token";
const OAUTH_CODE_CACHE_KEY = "oauth_code";
const USER_INFO_CACHE_KEY = "user_info_cache";

/** 从 URL query 读取 oauth_code */
export function getOAuthCodeFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get("oauth_code");
}

/** 从 Cookie 读取 token */
export function getCachedToken(): string | null {
  return getCookie(OAUTH_TOKEN_KEY);
}

/** 保存 token 到 Cookie */
export function setCachedToken(token: string): void {
  setCookie(OAUTH_TOKEN_KEY, token);
}

/** 清除所有 OAuth 缓存（localStorage + Cookie） */
export function clearOAuthCache(): void {
  localStorage.removeItem(OAUTH_CODE_CACHE_KEY);
  localStorage.removeItem(USER_INFO_CACHE_KEY);
  deleteCookie(OAUTH_TOKEN_KEY);
}

/** 跳转门户登录页 */
export function redirectToLogin(): void {
  window.location.href = `${PORTAL_HOME_URL}?login=${OAUTH_LOGIN_TAG}`;
}

/** 清空缓存并跳转登录 */
export function clearTokenAndRedirectToLogin(): void {
  clearOAuthCache();
  redirectToLogin();
}

/** 用 oauth_code 换取 token */
export async function getTokenByCode(code: string): Promise<TokenData> {
  const response = await fetch(`${APP_CONFIG.apiBaseUrl}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      code,
      grantType: "authorization_code",
      clientId: OAUTH_CLIENT_ID,
    }),
  });
  const data: SsoResponse<TokenData> = await response.json();
  if (data.success && data.data?.access_token) {
    return data.data;
  }
  throw new Error(data.msg || "Token request failed");
}

/** 检查 token 是否存在 */
export function isTokenValid(): boolean {
  return !!getCachedToken();
}

/** 清除 URL 中的 oauth_code 参数（用后即清，避免刷新重复换码） */
function cleanOAuthCodeFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete("oauth_code");
  window.history.replaceState({}, "", url.toString());
}

/**
 * 初始化 OAuth 认证流程（三级判断）
 * 1. Cookie 有 token -> 直接通过
 * 2. URL 有 oauth_code -> 换 token 后通过
 * 3. 都没有 -> 返回 false，由调用方跳转登录
 */
export async function initOAuth(): Promise<boolean> {
  if (getCachedToken()) {
    cleanOAuthCodeFromUrl();
    return true;
  }

  const urlCode = getOAuthCodeFromUrl();
  if (!urlCode) return false;

  try {
    const tokenData = await getTokenByCode(urlCode);
    setCachedToken(tokenData.access_token);
    cleanOAuthCodeFromUrl();
    return true;
  } catch {
    clearOAuthCache();
    return false;
  }
}

/** 获取登录用户信息（token 失效时降级用 localStorage 缓存） */
export async function getUserInfo(suppress401 = false): Promise<UserInfo | null> {
  const token = getCachedToken();
  if (!token) return null;

  try {
    const response = await fetch(`${APP_CONFIG.apiBaseUrl}/auth/user/info`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.status === 401) {
      if (!suppress401) clearTokenAndRedirectToLogin();
      return getCachedUserInfo();
    }

    const data: SsoResponse<UserInfo> = await response.json();
    if (isApiSuccessCode(data.code) && data.data) {
      localStorage.setItem(USER_INFO_CACHE_KEY, JSON.stringify(data.data));
      return data.data;
    }

    if (isUnauthorizedApiCode(data.code)) {
      if (!suppress401) clearTokenAndRedirectToLogin();
    }
    return getCachedUserInfo();
  } catch {
    return getCachedUserInfo();
  }
}

/** 读取 localStorage 中的用户信息缓存 */
export function getCachedUserInfo(): UserInfo | null {
  const raw = localStorage.getItem(USER_INFO_CACHE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserInfo;
  } catch {
    return null;
  }
}

// ---- 业务码判断 ----

export function isApiSuccessCode(
  code: string | number | undefined | null,
): boolean {
  if (code === undefined || code === null) return false;
  const n = typeof code === "string" ? parseInt(code, 10) : Number(code);
  return n === 0 || n === 200;
}

export function isUnauthorizedApiCode(
  code: string | number | undefined | null,
): boolean {
  if (code === undefined || code === null) return false;
  const n = typeof code === "string" ? parseInt(code, 10) : Number(code);
  return n === 401;
}
